<template>
  <v-form
    ref="form"
    class="accesslink-comments-form"
    data-cy="accessLink-view-comments-form"
    @submit.prevent="saveComment"
    :aria-label="formLabel"
  >
    <div v-if="!isImagePod" class="mb-4 d-flex accesslink-comments-in-out" :style="commentsInOutStyle">
      <label :id="inTimeLabel" class="time-label">
        {{ $t('routes.pod.menuComment.inTime') }}:
      </label>
      <div class="time-value">
        <v-text-field
          v-model="inTime"
          :rules="inOutTimeInputRules"
          :append-inner-icon="focusedInput === 'in' ? 'mdi-vector-link' : ''"
          @focus="inTimeOnFocus"
          @click:append="focusedInput = ''"
          hide-details="auto"
          density="compact"
          variant="solo-filled"
          data-cy="accessLink-view-comments-inTime"
          :aria-labelledby="inTimeLabel"
          block flat
        />
      </div>
      <div class="time-link d-flex justify-space-around">
        <v-tooltip
          location="bottom"
          :aria-label="!inOutTimesLinked ? $t('routes.accesslink.linkInOutTime') : $t('routes.accesslink.unlinkInOutTime')"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              size="small"
              :icon="!inOutTimesLinked ? 'mdi-link' : 'mdi-link-off'"
              :aria-label="!inOutTimesLinked ? $t('routes.accesslink.linkInOutTime') : $t('routes.accesslink.unlinkInOutTime')"
              @click="inOutTimesLinked = !inOutTimesLinked"
            />
          </template>
          <span>
            {{ !inOutTimesLinked ? $t('routes.accesslink.linkInOutTime') : $t('routes.accesslink.unlinkInOutTime') }}
          </span>
        </v-tooltip>
      </div>
      <label :id="outTimeLabel" class="time-label">
        {{ $t('routes.pod.menuComment.outTime') }}:
      </label>
      <div class="time-value">
        <v-text-field
          v-model="outTime"
          :rules="inOutTimeInputRules.concat(outTimeComparison)"
          :append-inner-icon="focusedInput === 'out' ? 'mdi-vector-link' : ''"
          @focus="outTimeOnFocus"
          @click:append="focusedInput = ''"
          hide-details="auto"
          density="compact"
          variant="solo-filled"
          data-cy="accessLink-view-comments-outTime"
          :aria-labelledby="outTimeLabel"
          block flat
        />
      </div>

      <div v-if="comment" class="flex-grow-1 text-right">
        <v-btn variant="text" icon="mdi-close" @click="closeForm" aria-label="Close" />
      </div>
    </div>
    <div v-else class="mb-4">
    </div>

    <div class="mb-4 px-2">
      <v-text-field
        v-model="email"
        v-bind:rules="emailRules"
        v-bind:error-messages="emailErrors"
        label="Email"
        hide-details="auto"
        :variant="comment ? 'outlined' : 'solo-filled'"
        required flat
        data-cy="accessLink-view-comments-email"
      />
    </div>

    <div class="mb-2 px-2">
      <v-textarea
        v-model="commentText"
        class="media-list-notes-textarea"
        :rules="commentTextRules"
        :error-messages="commentTextError"
        :label="$t('routes.accesslink.commentText')"
        @update:model-value="onCommentInput"
        counter="2048"
        rows="3"
        :variant="comment ? 'outlined' : 'solo-filled'"
        required persistent-counter flat
        data-cy="accessLink-view-comments-addComment"
      >
        <template v-slot:counter="{ value, max, counter }">
          <div role="status" :aria-label="`${value} of ${max} characters`">
            <span aria-hidden="true">{{ counter }}</span>
          </div>
        </template>
      </v-textarea>
    </div>

    <div class="text-center mb-2">
      <v-btn v-bind:disabled="isSubmitted" type="submit" color="primary" variant="flat"
             :elevation="0" data-cy="accessLink-view-comments-submit-btn">
        Submit comment
      </v-btn>

      <v-btn  v-if="comment && isImagePod" variant="flat" @click="closeForm" class="ml-2">
        Close
      </v-btn>
    </div>

    <div class="text-center mb-4 px-2">
      <div v-if="errorMessage" class="text-center mb-2 inline-message inline-message--error"
           role="alert"
           aria-live="polite"
           data-cy="accessLink-view-comments-errorMessage"
      >
        {{ errorMessage }}
      </div>

      <div v-if="successMessage" class="text-center mb-2 inline-message inline-message--success"
           role="alert"
           aria-live="polite"
           data-cy="accessLink-view-comments-successMessage"
      >
        {{ successMessage }}
      </div>
    </div>
  </v-form>
</template>

<script>
import { useCommonStore } from '@/stores/CommonStore.js'
import { usePlayerStore } from '@/stores/PlayerStore.js'
import { useAccesslinkStore } from '@/stores/AccesslinkStore';
import { toTimeMilliseconds } from '@/components/formatters.js';

import MimetypeHelper from '../../../../../components/MimetypeHelper.js'
import Helper from '@/components/helper.js';

let timeFieldLabelId = 0;
let formLabelId = 0;

export default {
  name: "AccesslinkCommentsForm",
  props: {
    pod: {
      type: Object,
      required: true,
    },
    comment: {
      type: Object,
      required: false,
    },
  },
  emits: ['inTimeFocus', 'outTimeFocus', "visibility"],
  data: function () {
    let self = this;

    return {
      email: "",
      emailErrors: [],
      emailRules: [
        v => !!v || this.$t('routes.pod.menuComment.invalidEmailAddress'),
        v => Helper.isEmailValid(v)|| this.$t('routes.pod.menuComment.invalidEmailAddress'),
      ],

      commentText: "",
      commentTextError: "",
      commentTextRules: [
        v => !!v || this.$t('routes.pod.menuComment.commentIsRequired'),
      ],

      isSubmitted: false,
      successMessage: "",
      errorMessage: "",
      focusedInput: "",
      inTime: "00:00.00",
      outTime: "",
      inOutTimeInputRules: [
        function (v) {
          if (!v) return true;
          if (!/^\d{2,}:\d{2}\.\d{2}$/g.test(v)) return self.$t('routes.pod.menuComment.invalidFormat');
          let seconds = v.split(".")[0].split(":")[1];
          if (parseInt(seconds) >= 60) return self.$t('routes.pod.menuComment.invalidSeconds');
          let videoDuration = (self.transcodedAsset && self.transcodedAsset.duration) ? parseFloat(self.transcodedAsset.duration) : 0;
          if (!videoDuration) return true;
          if ((self.videotimeToInt(v) / 1000) > videoDuration) return self.$t('routes.pod.menuComment.incorrectTime');
          return true;
        }
      ],
      inOutTimesLinked: false,
      labelId: ++timeFieldLabelId,
    };
  },
  mounted: function() {
    if (this.$route.query.e) {
      this.email = this.$route.query.e;
    }

    if (this.comment) {
      this.email = this.comment.email;
      this.commentText = this.comment.content;

      if (this.comment.start) {
        this.inTime = toTimeMilliseconds(this.comment.start);
      }

      if (this.comment.end) {
        this.outTime = toTimeMilliseconds(this.comment.end);
      }
    }
  },
  methods: {
    closeForm() {
      this.$emit('visibility', true);
    },
    onCommentInput: function() {
      const playerStore = usePlayerStore();
      playerStore.setPauseEvent(this.playerCURRENTTIME);
    },
    videotimeToInt: function(value) {
      let minutes = value.split(":")[0];
      let seconds = value.split(".")[0].split(":")[1];
      let milliseconds = value.split(".")[1];

      milliseconds = parseInt(parseFloat("0." + milliseconds) * 1000);
      seconds = parseInt(seconds) * 1000;
      minutes = parseInt(minutes) * 60000;
      return minutes + seconds + milliseconds;
    },
    updateFocusedInput: function(input) {
      this.focusedInput = input;
    },
    saveComment: async function() {
      this.successMessage = "";
      this.errorMessage = "";

      if (this.isSubmitted) {
        return;
      }

      let usedEmail = this.email;

      const validateFormResult = await this.$refs.form.validate();
      if (!validateFormResult.valid) {
        return;
      }

      const payload = {
        formData: {
          podid: this.pod.id,
          email: this.email,
          content: this.commentText,
          start: this.inTime !== '' ? this.videotimeToInt(this.inTime) : null,
          end: this.outTime ? this.videotimeToInt(this.outTime) : null,
        },
        recaptchaResponse: await this.$recaptcha(`accesslink_comment_${this.comment ? 'update' : 'create'}`),
      };
      const accesslinkStore = useAccesslinkStore();
      let VERB = accesslinkStore.addComment;

      if (this.comment) {
        VERB = accesslinkStore.updateComment;
        payload.id = this.comment.id;
      }

      if (this.isImagePod) {
        payload.formData.start = null;
        payload.formData.end = null;
      }

      this.isSubmitted = true;

      try {
        await VERB(payload);

        this.successMessage = "Your comment has been added!";
        this.$refs.form.reset();
        this.isSubmitted = false;
        this.$emit('visibility', false);

        this.inTime = "00:00.00";

        if (usedEmail) {
          this.email = usedEmail;
        } else if (this.$route.query.e) {
          this.email = this.$route.query.e;
        }
      } catch (error) {
        if (error &&
          error.response &&
          error.response.data &&
          error.response.data.error &&
          error.response.data.error.message) {
          this.errorMessage = error.response.data.error.message;
        } else {
          console.error(error);
        }

        this.isSubmitted = false;
      }
    },
    outTimeOnFocus() {
      this.focusedInput = 'out';

      if (this.outTime) {
        this.$emit('outTimeFocus', this.videotimeToInt(this.outTime) / 1000);
      }
    },
    inTimeOnFocus() {
      this.focusedInput = 'in';
      if (this.inTime) {
        this.$emit('inTimeFocus', this.videotimeToInt(this.inTime) / 1000);
      }
    }
  },
  watch: {
    playerCURRENTTIME: function(newTime) {
      const addingNewComment = !this.editComment && !this.replyComment;

      // if (addingNewComment && this.focusedInput == "in") {
      if (this.focusedInput === "in" || (addingNewComment && this.focusedInput !== "out")) {
        this.inTime = toTimeMilliseconds(newTime);
        if (this.outTime && this.inOutTimesLinked) {
          this.outTime = toTimeMilliseconds(newTime + 2);
        }
      }

      // if (addingNewComment && this.focusedInput == "out") {
      if (this.focusedInput === "out") {
        this.outTime = toTimeMilliseconds(newTime);
      }
    }
  },
  computed: {
    getPodStates: function () {
      const commonStore = useCommonStore();
      return commonStore.podStates;
    },
    isImagePod: function() {
      return MimetypeHelper.isMimetypeImage(this.pod);
    },
    outTimeComparison: function () {
      if (this.outTime && (this.videotimeToInt(this.inTime) >= this.videotimeToInt(this.outTime))) {
        return this.$t('routes.pod.menuComment.invalidOutTime');
      }
      return true;
    },
    transcodedAsset: function() {
      if (!this.pod ||
        !this.pod.asset ||
        !this.pod.asset.web ||
        !Array.isArray(this.pod.asset.web)) {
        return null;
      }

      // Get first web asset with status COMPLETE
      for(let i = 0; i < this.pod.asset.web.length; i++) {
        if (this.pod.asset.web[i].state === this.getPodStates.COMPLETE) {
          return this.pod.asset.web[i];
        }
      }

      return null;
    },
    playerCURRENTTIME: function() {
      const playerStore = usePlayerStore();
      return playerStore.currentTime;
    },
    getAccesslinkInfo: function() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getAccesslinkInfo;
    },
    getBrandingColor() {
      if (this.getAccesslinkInfo &&
        this.getAccesslinkInfo.brand &&
        this.getAccesslinkInfo.brand.color) {
        return this.getAccesslinkInfo.brand.color;
      }

      return null;
    },
    commentsInOutStyle() {
      let style = {};

      if (this.getBrandingColor) {
        style.backgroundColor = this.getBrandingColor + ' !important';
      }

      return style;
    },
    inTimeLabel() {
      return `accesslink-in-time-${this.labelId}-label`;
    },
    outTimeLabel() {
      return `accesslink-out-time-${this.labelId}-label`;
    },
    formLabel() {
      return this.comment ? `Comment #${++formLabelId}` : 'New comment';
    },
  }
}
</script>

<style lang="scss" scoped>
.accesslink-comments {
  &-form {
    border: 1px solid #CBCBCB;
    border-radius: 20px;

    :global(.theme-light &) {
      background-color: #FFFFFF;
    }

    :global(.theme-dark &) {
      background-color: #232b36;
    }
  }

  &-in-out {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    background-color: #3dabff;
    border-radius: 20px;

    .time-value {
      width: 27%;
    }

    .time-label {
      padding-right: 8px;
      text-align: right;
    }

    .time-link {
      margin-left: 8px;
      margin-top: 2px;
    }
  }
}
</style>
